import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import * as ActionTypes from '../actions/types';

type AnswerState = {
    answer?:
        | {
              policyType: string;
              claimCategory: string;
              formType: string;
              answerFromGroupSteps: any;
              activeGroupStepCode: any | string;
              currency: any | string;
              loadingAddresses: any | Array<string>;
              policyTypeCode?: string;
              claimApplicationId?: string;
              answers?: Array<any>;
              claimCategoryCode?: string;
              formTypeCode?: string;
          }
        | any;
    draftClaimApplicationList?: any | Array<any>;
    loading?: boolean;
    submittedLoading?: boolean;
    errorMessage?: any | string;
    loadingNoSubmittedDraft?: boolean | undefined;
};
type ChangePayload = {
    name: string;
    value: string;
};

type DraftClaimApplicationByUsePayload = {
    body: {
        data: {
            policyType: string;
            policyTypeCode: string;
            claimCategory: string;
            formType: string;
            answerFromGroupSteps: Array<any>;
            claimApplicationId: string;
            answers: Array<any>;
            claimCategoryCode: string;
            formTypeCode: string;
            activeGroupStepCode: string;
        };
    };
};
const INITIAL_STATE: AnswerState = {
    answer: {
        policyType: '',
        claimCategory: '',
        formType: '',
        answerFromGroupSteps: {},
        activeGroupStepCode: null,
        currency: null,
        loadingAddresses: null,
    },
    draftClaimApplicationList: null,
    loading: false,
    submittedLoading: false,
    errorMessage: null,
    loadingNoSubmittedDraft: false,
};

const changeAnswerState = (
    state: AnswerState,
    action: PayloadAction<ChangePayload>,
): any => {
    return {
        ...state,
        answer: {
            ...state.answer,
            [action.payload.name]: action.payload.value,
        },
    };
};

const redirectAfterClaimSubmit = (state: AnswerState) => {
    return {
        ...state,
        answer: { ...INITIAL_STATE.answer },
    };
};

const fetchDraftClaimApplicationByUser = (state: AnswerState) => {
    return {
        ...state,
        submittedLoading: true,
        loadingNoSubmittedDraft: true,
    };
};

const fetchDraftClaimApplicationByUserSuccess = (
    state: AnswerState,
    action: PayloadAction<any>,
) => {
    const { data } = action.payload.body;
    if (!data) {
        return {
            ...state,
            submittedLoading: false,
            loadingNoSubmittedDraft: false,
        };
    }
    const answerWithCurrency =
        data.answers.length > 0
            ? data.answers.find(
                  (answer: { currency: string }) => answer.currency,
              )
            : null;
    const newState = {
        policyType: data && data.policyTypeCode ? data.policyTypeCode : '',
        claimCategory:
            data && data.claimCategoryCode ? data.claimCategoryCode : '___',
        formType: data && data.formTypeCode ? data.formTypeCode : '___',
        answerFromGroupSteps: data.answers,
        claimApplicationId: data.claimApplicationId,
    };

    return {
        ...state,
        answer: {
            ...newState,
            activeGroupStepCode:
                data && data.activeGroupStepCode
                    ? data.activeGroupStepCode
                    : null,
            currency: answerWithCurrency ? answerWithCurrency.currency : null,
        },
        submittedLoading: false,
        loadingNoSubmittedDraft: false,
    };
};
const fetchDraftClaimApplicationByUserFailed = (state: AnswerState) => {
    return {
        ...state,
        loading: false,
        loadingNoSubmittedDraft: false,
    };
};

const fetchNoSubmittedDraftClaimApplicationByUser = (state: AnswerState) => {
    return {
        ...state,
        loading: true,
        loadingNoSubmittedDraft: true,
    };
};

const fetchNoSubmittedDraftClaimApplicationByUserSuccess = (
    state: AnswerState,
    action: PayloadAction<DraftClaimApplicationByUsePayload>,
) => {
    const { data } = action.payload.body;
    if (!data) {
        return {
            ...state,
            loading: false,
            loadingNoSubmittedDraft: false,
        };
    }
    const answerWithCurrency =
        data.answers.length > 0
            ? data.answers.find(
                  (answer: { currency: string }) => answer.currency,
              )
            : null;
    const newState = {
        policyType: data && data.policyTypeCode ? data.policyTypeCode : '',
        claimCategory:
            data && data.claimCategoryCode ? data.claimCategoryCode : '___',
        formType: data && data.formTypeCode ? data.formTypeCode : '___',
        answerFromGroupSteps: data.answers,
        claimApplicationId: data.claimApplicationId,
    };

    return {
        ...state,
        answer: {
            ...newState,
            activeGroupStepCode:
                data && data.activeGroupStepCode
                    ? data.activeGroupStepCode
                    : null,
            currency: answerWithCurrency ? answerWithCurrency.currency : null,
        },
        loading: false,
        loadingNoSubmittedDraft: false,
    };
};
const fetchNoSubmittedDraftClaimApplicationByUserFailed = (
    state: AnswerState,
) => {
    return {
        ...state,
        loading: false,
        loadingNoSubmittedDraft: false,
    };
};
const clearAnswersFromGroupSteps = (state: AnswerState) => {
    return {
        ...state,
        answer: {
            ...state.answer,
            answerFromGroupSteps: {},
        },
    };
};

const fetchDraftClaimApplicationListByUser = (state: AnswerState) => {
    return {
        ...state,
        loading: true,
    };
};

const fetchDraftClaimApplicationListByUserSuccess = (
    state: any,
    action: PayloadAction<DraftClaimApplicationByUsePayload>,
) => {
    const { data } = action.payload.body;
    return {
        ...state,
        draftClaimApplicationList: data,
        loading: false,
    };
};

const fetchDraftClaimApplicationListByUserFailed = (state: AnswerState) => {
    return {
        ...state,
        loading: false,
    };
};

const clearAnswersState = (state: AnswerState) => {
    return {
        ...state,
        answer: {
            policyType: '',
            claimCategory: '',
            formType: '',
            answerFromGroupSteps: {},
            activeGroupStepCode: null,
            currency: null,
            loadingAddresses: null,
        },
    };
};

const clearPolicyTypeCombinationState = (state: AnswerState) => {
    return {
        ...state,
        answer: {
            policyType: '',
            claimCategory: '',
            formType: '',
            answerFromGroupSteps: {},
            activeGroupStepCode: null,
            currency: null,
            loadingAddresses: null,
        },
    };
};

const answer = createReducer(INITIAL_STATE, {
    [ActionTypes.CHANGE_ANSWER_STATE]: changeAnswerState,
    [ActionTypes.REDIRECT_AFTER_CLAIM_SUBMIT]: redirectAfterClaimSubmit,
    [ActionTypes.GET_DRAFT_CLAIM_APPLICATION_BY_USER]:
        fetchDraftClaimApplicationByUser,
    [ActionTypes.GET_DRAFT_CLAIM_APPLICATION_BY_USER_SUCCESS]:
        fetchDraftClaimApplicationByUserSuccess,
    [ActionTypes.GET_DRAFT_CLAIM_APPLICATION_BY_USER_FAILED]:
        fetchDraftClaimApplicationByUserFailed,
    [ActionTypes.GET_NO_SUBMITTED_DRAFT_CLAIM_APPLICATION_BY_USER]:
        fetchNoSubmittedDraftClaimApplicationByUser,
    [ActionTypes.GET_NO_SUBMITTED_DRAFT_CLAIM_APPLICATION_BY_USER_SUCCESS]:
        fetchNoSubmittedDraftClaimApplicationByUserSuccess,
    [ActionTypes.GET_NO_SUBMITTED_DRAFT_CLAIM_APPLICATION_BY_USER_FAILED]:
        fetchNoSubmittedDraftClaimApplicationByUserFailed,
    [ActionTypes.GET_DRAFT_CLAIM_APPLICATION_LIST_BY_USER]:
        fetchDraftClaimApplicationListByUser,
    [ActionTypes.GET_DRAFT_CLAIM_APPLICATION_LIST_BY_USER_SUCCESS]:
        fetchDraftClaimApplicationListByUserSuccess,
    [ActionTypes.GET_DRAFT_CLAIM_APPLICATION_LIST_BY_USER_FAILED]:
        fetchDraftClaimApplicationListByUserFailed,
    [ActionTypes.CLEAR_ANSWERS_FROM_GROUP_STEPS]: clearAnswersFromGroupSteps,
    [ActionTypes.CLEAR_ANSWERS_STATE]: clearAnswersState,
    [ActionTypes.CLEAR_POLICY_TYPE_COMBINATION]:
        clearPolicyTypeCombinationState,
});

export default answer;
